import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const GROUPS = [
  ["candidates", "Candidates"],
  ["jobs", "Jobs"],
  ["applications", "Applications"],
  ["interviews", "Interviews"],
  ["hackathons", "Hackathons"],
  ["career_events", "Career Events"],
];

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const timer = useRef();
  const rootRef = useRef();

  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setData(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/global-search", { params: { q } });
        setData(data);
      } catch { setData({ query: q, total: 0, results: {} }); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div ref={rootRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input
          value={q} onChange={(e)=>{ setQ(e.target.value); setOpen(true); }}
          onFocus={()=>setOpen(true)}
          placeholder="Search candidates, jobs, applications…"
          className="w-full bg-slate-800 border border-slate-700 text-white rounded-md pl-9 pr-3 py-2 text-sm focus:border-[#F59E0B] focus:ring-1 focus:ring-[#F59E0B]/40 outline-none"
          data-testid="global-search-input"
        />
        {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin"/>}
      </div>
      {open && q.trim().length >= 2 && (
        <div className="absolute z-40 mt-2 left-0 right-0 bg-slate-900 border border-slate-700 rounded-md shadow-lg max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {data == null && loading && <div className="p-4 text-slate-400 text-sm">Searching…</div>}
          {data && data.total === 0 && !loading && <div className="p-4 text-slate-400 text-sm">No results for "{q}".</div>}
          {data && GROUPS.map(([key, label]) => {
            const items = data.results?.[key] || [];
            if (items.length === 0) return null;
            return (
              <div key={key} className="border-b border-slate-800 last:border-0">
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#F59E0B] font-semibold">{label}</div>
                {items.map(item => (
                  <Link key={item.id} to={item.link} onClick={()=>setOpen(false)}
                    className="block px-3 py-2 hover:bg-slate-800/60" data-testid={`gs-${key}-${item.id}`}>
                    <div className="text-white text-sm truncate">{item.title}</div>
                    <div className="text-xs text-slate-400 truncate">{item.subtitle} {item.status && <span className="text-slate-500">· {item.status}</span>}</div>
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
